import PixDonate from '@/components/PixDonate'
import SocialIcons from '@/components/SocialIcons'
import { formatSlot, hasWeekdaySlot, hasWeekendSlot, sortSchedule } from '@/lib/time'
import type { Partner, RadioSettings, SocialLink, TeamMember } from '@/types/radio'
import {CalendarDays, MapPin, Mic2} from 'lucide-react'

interface ScheduleAndPromoProps {
  team: TeamMember[]
  settings: RadioSettings | null
  social: SocialLink[]
  partners: Partner[]
}

function ScheduleBlock({ title, items }: { title: string; items: TeamMember[] }) {
  if (!items.length) return null
  return (
    <div>
      <p className="mb-2 text-[11px] font-extrabold tracking-[0.18em] text-[#ffd21f]">{title}</p>
      <div className="space-y-3">
        {items.map((item) => (
          <div key={`${title}-${item._id || item.programName}-${item.startTime}`} className="grid grid-cols-[92px_1fr] gap-2 border-b border-white/10 pb-3 last:border-0 sm:grid-cols-[110px_1fr] sm:gap-3">
            <p className="text-sm font-extrabold text-[#ffd21f]">{formatSlot(item.startTime, item.endTime)}</p>
            <div>
              <p className="font-bold leading-tight">{item.programName}</p>
              <p className="text-sm text-white/70">{item.name}</p>
              {(item.days || []).length ? (
                <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/50">
                  {(item.days || []).join(' · ')}
                </p>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function ScheduleAndPromo({ team, settings, social, partners }: ScheduleAndPromoProps) {
  const active = (team || []).filter((item) => item?.active)
  const weekday = sortSchedule(active.filter((item) => hasWeekdaySlot(item.days)))
  const weekend = sortSchedule(active.filter((item) => hasWeekendSlot(item.days)))

  return (
    <section id="programacao" className="mx-auto mt-10 max-w-7xl px-3 pb-16 sm:px-4 lg:px-6">
      <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr_1.05fr]">
        <article className="rounded-[28px] bg-[#071a7d] p-5 shadow-xl">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-[#ffd21f] text-[#071a63]">
              <CalendarDays size={20} />
            </span>
            <h2 className="font-display text-2xl font-black leading-tight">PROGRAMAÇÃO<br />COMPLETA</h2>
          </div>
          <div className="space-y-6">
            <ScheduleBlock title="DURANTE A SEMANA" items={weekday} />
            <ScheduleBlock title="FIM DE SEMANA" items={weekend} />
            {!weekday.length && !weekend.length ? (
              <p className="text-sm text-white/70">A programação ainda não foi cadastrada.</p>
            ) : null}
          </div>
          <a href="#equipe" className="mt-4 inline-flex text-sm font-extrabold text-[#ffd21f]">
            VER TODA A PROGRAMAÇÃO {'>'}
          </a>
        </article>

        <div className="grid gap-4">
          <article id="sobre" className="rounded-[28px] bg-gradient-to-br from-[#ffd21f] to-[#f5b400] p-5 text-[#12307a]">
            <div className="mb-3 flex items-center gap-2">
              <Mic2 />
              <h2 className="font-display text-2xl font-black">FIQUE LIGADO!</h2>
            </div>
            <p className="text-sm leading-relaxed">
              {settings?.description || 'Aqui você encontra muita música, informação, entretenimento e o melhor da nossa região!'}
            </p>
            <p className="mt-3 text-xs font-bold uppercase tracking-wide">
              Horário de funcionamento: {settings?.hours || '24 horas no ar'}
            </p>
          </article>

          <PixDonate settings={settings} />
        </div>

        <article id="contato" className="relative overflow-hidden rounded-[28px] min-h-[340px]">
          <img
            src={settings?.cityImageUrl || 'https://static.lumi.new/af/af7317e7ee9efb052f38b323473c9508.png'}
            alt={settings?.city || 'Cidade'}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#071a63] via-[#071a63]/20 to-transparent" />
          <div className="relative z-10 flex h-full flex-col justify-between p-5">
            <p className="inline-flex w-fit items-center gap-2 rounded-full bg-[#0b2ea8]/80 px-3 py-1 text-xs font-extrabold">
              <MapPin size={14} /> {settings?.city || 'SÃO JOSÉ DO SERIDÓ/RN'}
            </p>
            <div>
              <p className="font-script text-3xl">Aqui é Bonita!</p>
              <SocialIcons links={social} className="mt-3" />
            </div>
          </div>
        </article>
      </div>

      {partners?.length ? (
        <div className="mt-8 rounded-[24px] bg-white/5 p-4">
          <p className="mb-3 text-xs font-extrabold tracking-[0.2em] text-white/70">PARCEIROS</p>
          <div className="flex flex-wrap gap-3">
            {partners.filter((item) => item.active).map((partner) => (
              <a
                key={partner._id || partner.name}
                href={partner.url}
                target="_blank"
                rel="noreferrer"
                className="rounded-full bg-white/10 px-4 py-2 text-sm font-semibold hover:bg-[#ffd21f] hover:text-[#0b2ea8]"
              >
                {partner.name}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default ScheduleAndPromo
